<template>

  <div class="position-relative">

    <div class="mt-14 mb-2 d-flex align-center">

      <div>

        <v-btn
          class="mr-4"
          variant="outlined"
          color="text-high-emphasis"
          @click="calendarDate = Temporal.Now.plainDateISO().toString()"
          :text="$t('titles.today')"
        />

      </div>

      <div>

        <v-btn
          variant="text"
          color="text-high-emphasis"
          icon="$calendarPrev"
          @click="calendar?.prev()"
        />

        <v-btn
          variant="text"
          color="text-high-emphasis"
          icon="$calendarNext"
          @click="calendar?.next()"
        />

      </div>

      <div class="text-h5">
        {{ calendar?.title }}
      </div>

    </div>

    <v-defaults-provider :defaults="{VBtn: {color: null}}">
      <v-calendar
        ref="calendar"
        v-model="calendarDate"
        :events="events"
        :disabled="loading"
      >

        <template v-slot:event="{ event }">

          <v-sheet v-if="event" rounded class="pa-0 mx-1 text-center text-body-2" :color="(event as ActivityEvent).color">
            <p class="text-body-2">{{ event.title }}</p>
            <v-overlay v-if="event.type === 'max_on_lot' && event.maxOnLotDetails && (event as ActivityEvent).maxOnLotDetails!.window_start" activator="parent" open-on-hover :scrim="false" location-strategy="connected">
              <v-sheet class="pa-4">
                {{ formatWindow((event as ActivityEvent).maxOnLotDetails!.window_start!) }}
                <table class="calendar-popup-table mt-2 text-body-2">
                  <tbody>
                    <tr v-for="[locationId, count] in Object.entries((event as ActivityEvent).maxOnLotDetails!.by_location)">
                      <td>{{ locationNames.get(parseInt(locationId, 10)) ?? locationId }}</td>
                      <td class="text-end">{{ count }}</td>
                    </tr>
                  </tbody>
                </table>
              </v-sheet>
            </v-overlay>
          </v-sheet>

        </template>

      </v-calendar>
    </v-defaults-provider>

    <v-overlay v-model="loading" class="align-center justify-center mt-16" persistent contained>
      <v-progress-circular
        color="primary"
        indeterminate
        size="64"
      />
    </v-overlay>

  </div>

</template>

<script setup lang="ts">

import { computed, ref, useTemplateRef, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDate, useTheme } from 'vuetify'

import { VCalendar } from 'vuetify/components'

import Location from '../../../../models/Location'

import axios from 'axios'
import { ReportingApi, type ReportingGetActivityResponse, type ReportingGetActivityResponseEntry } from '../../../../services/api-clients/ReportingApi'
import NotificationService from '../../../../services/NotificationService'
import { Temporal } from '@js-temporal/polyfill'
import type { DateRange } from '../../../../types/misc'
import type { VCalendarEvent } from '../../../../types/vuetify'

interface Props {
  locationIds: number[]
  channels?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  channels: () => [],
})

const VuetifyDate = useDate()
const themeColors = useTheme().current.value.colors
const { t } = useI18n()

const calendar = useTemplateRef('calendar')

const locationNames = computed(() => {
  const map = new Map<number, string>()
  Location.query().whereIdIn(props.locationIds).get().forEach((location: Location) => {
    map.set(location.ID, location.locationName)
  })
  return map
})

/**
 *
 */

const calendarDate = ref<string>(Temporal.Now.plainDateISO().toString())

const dateRange = computed<DateRange>(() => {

  const cd = Temporal.PlainDate.from(calendarDate.value)
  return {
    start: cd.with({day: 1}),
    end: cd.with({day: cd.daysInMonth}),
  }

  // const cd = calendarDate.value
  // const temporalCalendarDate = Temporal.PlainDate.from({year: VuetifyDate.getYear(cd), month: VuetifyDate.getMonth(cd) + 1, day: VuetifyDate.getDate(cd)})
  // return {
  //   start: temporalCalendarDate.with({day: 1}),
  //   end: temporalCalendarDate.with({day: temporalCalendarDate.daysInMonth}),
  // }

})

/**
 * Raw data
 */

const loading = ref<boolean>(false)
const rawData = ref<null | ReportingGetActivityResponse>(null)

const abortControllers: AbortController[] = []

watchEffect(() => {

  if (!props.locationIds.length) return

  abortControllers.forEach(ac => ac.abort())
  abortControllers.length = 0

  loading.value = true

  const abortController = new AbortController()
  abortControllers.push(abortController)

  ReportingApi.getActivity(props.locationIds, dateRange.value.start, dateRange.value.end, abortController.signal)
    .then(result => {
      rawData.value = result
      loading.value = false
    })
    .catch(e => {
      if (axios.isCancel(e)) {
        return
      }
      console.error(e)
      NotificationService.error('Couldn\'t load data!')
      loading.value = false
    })

})

/**
 * Calendar events
 */

interface ActivityEvent extends VCalendarEvent {
  type: 'arrivals' | 'departures' | 'max_on_lot'
  // title: string
  // start: Date
  // end: Date
  // allDay: boolean
  // color: string
  maxOnLotDetails?: ReportingGetActivityResponseEntry['max_on_lot']
}

const midnight = new Temporal.PlainTime(0, 0, 0)

const events = computed<ActivityEvent[]>(() => {

  const output: ActivityEvent[] = []

  if (rawData.value) {

    Object.entries(rawData.value).forEach(([isoDate, entry]: [string, ReportingGetActivityResponseEntry]) => {

      // const date = VuetifyDate.date(isoDate) as DateInstance
      const date = Temporal.PlainDate.from(isoDate)
      const start = date.toPlainDateTime(midnight)
      const end = start

      const vuetifyStart = VuetifyDate.date(start.toString())
      const vuetifyEnd = VuetifyDate.date(end.toString())

      const arrivals = Object.entries(entry.arrivals.by_channel)
        .filter(([channel, _value]) => props.channels.length === 0 || props.channels.includes(channel))
        .reduce((subtotal, [_channel, value]) => subtotal + value, 0)

      const departures = Object.entries(entry.departures.by_channel)
        .filter(([channel, _value]) => props.channels.length === 0 || props.channels.includes(channel))
        .reduce((subtotal, [_channel, value]) => subtotal + value, 0)

      output.push({
        type: 'arrivals',
        color: themeColors.primary,
        title: `${t('titles.arrivals')}: ${arrivals}`,
        start: vuetifyStart,
        end: vuetifyEnd,
        allDay: true,
      })

      output.push({
        type: 'departures',
        title: `${t('titles.departures')}: ${departures}`,
        start: vuetifyStart,
        end: vuetifyEnd,
        color: themeColors.secondary,
        allDay: true,
      })

      output.push({
        type: 'max_on_lot',
        title: `${t('titles.max_on_lot')}: ${entry.max_on_lot.total}`,
        start: vuetifyStart,
        end: vuetifyEnd,
        allDay: true,
        color: themeColors.success,
        maxOnLotDetails: entry.max_on_lot,
      })

    })

  }

  return output

})

/**
 * Miscellaneous
 */

function formatWindow(windowStart: Temporal.PlainTime): string {
  const windowEnd = windowStart.add({minutes: 30})
  const formattedStart = windowStart.toLocaleString('en-US', {timeStyle: 'short'})
  const formattedEnd = windowEnd.toLocaleString('en-US', {timeStyle: 'short'})
  return `${formattedStart} – ${formattedEnd}`
}

</script>
